import React, { useEffect, useRef } from 'react';
import {
  Home,
  BarChart3,
  FileText,
  Receipt,
  ArrowLeftRight,
  TrendingUp,
  Import,
  Settings,
  Users,
  Shield,
  CheckSquare,
  MessageSquare,
  Activity,
  X,
  UserCheck,
  Folder,
  ClipboardCheck,
  Briefcase,
  Building2,
  Bell,
  CalendarDays,
} from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../../contexts/AuthContext';
import { dashboardService } from '../../services/dashboardService';
import {
  STALE_TIME_MS,
  defaultRefetchPolicy,
  queryKeys,
  type TransactionListFiltersKey,
} from '../../config/reactQuery';
import { transactionsService } from '../../services/transactionsService';
import { getDefaultListPeriod } from '../../utils/listPeriodDefaults';
import { useCAFirmAccess } from '../../hooks/useCAFirmAccess';
import { SidebarNavItem, type SidebarNavItemConfig } from './sidebar/SidebarNavItem';
import { sidebarShell } from './sidebar/sidebarNavTokens';

interface SidebarProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  collapsed: boolean;
  setCollapsed: (collapsed: boolean) => void;
}

const Sidebar: React.FC<SidebarProps> = ({ open, setOpen, collapsed, setCollapsed }) => {
  const queryClient = useQueryClient();
  const { organization, workspaceMode } = useAuth();
  const { canManageStaff } = useCAFirmAccess();
  const dashboardPrefetchOrgRef = useRef<string | null>(null);

  useEffect(() => {
    dashboardPrefetchOrgRef.current = null;
  }, [organization?.id]);

  const prefetchDashboardData = () => {
    if (!organization?.id) return;
    const orgId = organization.id;
    if (dashboardPrefetchOrgRef.current === orgId) return;
    dashboardPrefetchOrgRef.current = orgId;

    const y = new Date().getFullYear();
    const opts = { staleTime: STALE_TIME_MS.dashboard, ...defaultRefetchPolicy };

    void queryClient.prefetchQuery({
      queryKey: queryKeys.dashboardOverview(orgId, y),
      queryFn: () => dashboardService.getOverview(y, orgId),
      ...opts,
    });
    void queryClient.prefetchQuery({
      queryKey: queryKeys.dashboardMonthly(orgId, y),
      queryFn: () => dashboardService.getMonthly(y, orgId),
      ...opts,
    });
  };

  const prefetchTransactionsList = () => {
    if (!organization?.id) return;
    const { year, month } = getDefaultListPeriod();
    const prefetchFilters: TransactionListFiltersKey = {
      limit: 20,
      type: 'all',
      category: 'all',
      paymentMethod: 'all',
      startDate: '',
      endDate: '',
      month,
      year,
      search: '',
    };
    const txnOpts = { staleTime: STALE_TIME_MS.transactionsList, ...defaultRefetchPolicy };
    void queryClient.prefetchQuery({
      queryKey: queryKeys.transactionsList(organization.id, 1, prefetchFilters),
      queryFn: () =>
        transactionsService.getTransactions({
          page: 1,
          limit: 20,
          type: 'all',
          category: 'all',
          payment_method: 'all',
          month,
          year,
          organizationId: organization.id,
          skipCache: true,
        }),
      ...txnOpts,
    });
  };

  const businessNavigation: SidebarNavItemConfig[] = [
    { name: 'Dashboard', href: '/dashboard', icon: Home },
    { name: 'Reports', href: '/reports', icon: BarChart3 },
    { name: 'Transactions', href: '/transactions', icon: Receipt },
    { name: 'Receivables & Payables', href: '/receivables-payables', icon: ArrowLeftRight },
    { name: 'GST Compliance', href: '/gst', icon: FileText },
    { name: 'Statutory Dues', href: '/gst/statutory', icon: Shield },
    { name: 'Cashflow Analysis', href: '/cashflow', icon: TrendingUp },
    { name: 'Tasks & Workflow', href: '/tasks', icon: CheckSquare },
    { name: 'Approvals', href: '/approvals', icon: ClipboardCheck },
    { name: 'Contacts', href: '/contacts', icon: UserCheck },
    { name: 'Document Management', href: '/documents', icon: Folder },
    { name: 'Comment History', href: '/comment-history', icon: MessageSquare },
    { name: 'Audit Logs', href: '/audit-logs', icon: Activity },
    { name: 'Data Import', href: '/import', icon: Import },
    { name: 'Settings', href: '/settings', icon: Settings },
  ];

  const caNavigation: SidebarNavItemConfig[] = [
    { name: 'CA Dashboard', href: '/ca/dashboard', icon: Briefcase },
    { name: 'Client Organizations', href: '/ca/clients', icon: Building2 },
    { name: 'Compliance Calendar', href: '/ca/compliance/calendar', icon: CalendarDays },
    { name: 'Notifications', href: '/ca/notifications', icon: Bell },
    ...(canManageStaff ? [{ name: 'Staff Management', href: '/ca/staff', icon: Users }] : []),
    { name: 'GST Dashboard', href: '/ca/gst', icon: FileText },
    { name: 'Settings', href: '/settings', icon: Settings },
  ];

  const navigation = workspaceMode === 'ca' ? caNavigation : businessNavigation;

  const handleNavPrefetch = (itemName: string) => {
    if (itemName === 'Dashboard') prefetchDashboardData();
    if (itemName === 'Transactions') prefetchTransactionsList();
  };

  return (
    <>
      <div
        className={`hidden lg:fixed lg:inset-y-0 lg:z-30 lg:flex lg:flex-col transition-[width] duration-300 ease-in-out ${
          collapsed ? 'lg:w-20' : 'lg:w-64'
        }`}
      >
        <div className={`${sidebarShell.desktop} pt-4 sm:pt-6 pb-6`}>
          <div className={sidebarShell.desktopAmbient} aria-hidden />
          <div
            className={`relative z-10 mb-5 flex flex-shrink-0 sm:mb-6 ${
              collapsed ? 'justify-center px-2' : 'px-4'
            }`}
          >
            <button
              type="button"
              onClick={() => setCollapsed(!collapsed)}
              className={`rounded-lg transition-opacity duration-200 hover:opacity-85 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500/30 ${
                collapsed ? 'flex w-full justify-center p-1' : 'block w-full text-left'
              }`}
              title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
              aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
              aria-expanded={!collapsed}
            >
              <img
                src="/cmndf-logo.png"
                alt="cmnd[f]."
                className={
                  collapsed
                    ? 'mx-auto h-auto max-h-10 w-full max-w-[3.25rem] object-contain'
                    : 'h-auto max-h-[4.75rem] w-full max-w-[13.75rem] object-contain object-left sm:max-h-[5.25rem]'
                }
                onError={(e) => {
                  e.currentTarget.style.display = 'none';
                }}
              />
            </button>
          </div>

          <nav
            className={`relative z-10 flex-1 space-y-1 overflow-y-auto pb-2 ${
              collapsed ? 'px-2' : 'px-3'
            }`}
          >
            {navigation.map((item) => (
              <SidebarNavItem
                key={item.name}
                item={item}
                collapsed={collapsed}
                variant="desktop"
                onNavigate={() => setOpen(false)}
                onMouseEnter={() => handleNavPrefetch(item.name)}
              />
            ))}
          </nav>
        </div>
      </div>

      <div className={`lg:hidden fixed inset-0 z-50 ${open ? 'block' : 'hidden'}`}>
        <div className={sidebarShell.mobileOverlay} onClick={() => setOpen(false)} aria-hidden />
        <div className={sidebarShell.mobile}>
          <div className="absolute top-0 right-0 -mr-10 pt-2 sm:-mr-12">
            <button
              type="button"
              className="ml-1 flex h-9 w-9 items-center justify-center rounded-full bg-white text-slate-600 shadow-lg transition-colors duration-200 hover:bg-slate-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500/30 touch-manipulation sm:h-10 sm:w-10"
              onClick={() => setOpen(false)}
            >
              <span className="sr-only">Close sidebar</span>
              <X className="h-5 w-5" strokeWidth={1.75} />
            </button>
          </div>

          <div className="relative z-10 mb-4 flex flex-shrink-0 items-center px-4 pt-5">
            <img
              src="/cmndf-logo.png"
              alt="cmnd[f]."
              className="h-auto max-h-[4.75rem] w-auto max-w-[min(100%,20rem)] object-contain object-left"
              onError={(e) => {
                e.currentTarget.style.display = 'none';
              }}
            />
          </div>

          <nav className="relative z-10 flex-1 space-y-1 overflow-y-auto px-3 pb-6 mobile-safe-area-bottom">
            {navigation.map((item) => (
              <SidebarNavItem
                key={item.name}
                item={item}
                variant="mobile"
                onNavigate={() => setOpen(false)}
                onMouseEnter={() => handleNavPrefetch(item.name)}
              />
            ))}
          </nav>
        </div>
      </div>
    </>
  );
};

export default Sidebar;
